import { c as _c } from "react/compiler-runtime";
import { type MessageComposerHandle, type SlashCommand } from '@precis-digital/kurama/MessageLayout';
import { useCallback, useMemo, type ReactElement, type RefObject } from 'react';
import { makeErrorToast } from 'shared/components/Toaster';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useSkills } from 'shared/copilot/api/skills';
import { buildCopilotSkillSlashCommands, CopilotComposerSkillsAction } from 'shared/copilot/components/CopilotComposerSkillsAction';
import { type FlowDatasourceEntityType } from 'shared/copilot/flowDataAgentConstants';
import { type CopilotComposerSubmit } from 'shared/copilot/types';
import { useTranslation } from 'shared/translations';
import { type Skill } from 'src/copilot/types/skills';
import { getSkillChipColor } from 'src/copilot/utils/mapFlowSkill';
import { getMissingPlatformsForSkill, getPlatformDisplayName, getSelectedPlatformsFromEntityTypes, hasComposerSkills } from 'src/copilot/utils/skills';
import { getUnavailableChipSkills, resolveComposerSkillOutbound } from 'src/copilot/utils/skillTags';
interface UseCopilotComposerSkillsArgs {
  enableSkills: boolean;
  inputPlaceholder: string;
  skillsInputPlaceholder?: string;
  selectedEntityTypes: FlowDatasourceEntityType[];
  composerRef?: RefObject<MessageComposerHandle | null>;
  onSubmit: CopilotComposerSubmit;
}
interface UseCopilotComposerSkillsResult {
  inputPlaceholder: string;
  slashCommands: SlashCommand[];
  /** Shown in the `/` menu while there are no skills to offer (loading, error, empty catalog); `undefined` when skills exist. */
  slashCommandsEmptyMessage: string | undefined;
  /** Skills composer action; `null` when skills are disabled. */
  skillsAction: ReactElement | null;
  /** `MessageComposer` `onBeforeSubmit`. */
  handleBeforeSubmit: (message: string, meta: {
    slashCommandIds: Array<string | number>;
  }) => boolean;
  /** `MessageComposer` `onSubmit`. */
  handleSubmit: (message: string, meta?: {
    slashCommandIds?: Array<string | number>;
  }) => void;
}

/**
 * Composer skills wiring for `CopilotChatShell`: catalog, placeholder, `/` commands,
 * the Skills action, the submit gate for unavailable chips, and the outbound rewrite
 * (Flow skill tags + pin ids). With `enableSkills: false` it only neutralizes typed tags.
 */
export const useCopilotComposerSkills = t0 => {
  const $ = _c(47);
  const {
    enableSkills,
    inputPlaceholder,
    skillsInputPlaceholder,
    selectedEntityTypes,
    composerRef,
    onSubmit
  } = t0;
  const {
    currentClient
  } = useCurrentClient();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "copilot"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("common", t1);
  const {
    data: remoteSkills,
    isLoading: isSkillsLoading,
    isFetching: isSkillsFetching,
    isError: isSkillsError,
    refetch: refetchSkills
  } = useSkills(currentClient.id, enableSkills);
  let t2;
  if ($[1] !== remoteSkills) {
    t2 = remoteSkills ?? [];
    $[1] = remoteSkills;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const skillsCatalog = t2;
  const isSkillsCatalogUnavailable = isSkillsError && remoteSkills == null;
  const showSkillsLoading = isSkillsLoading || isSkillsFetching && remoteSkills == null;
  let t3;
  if ($[3] !== selectedEntityTypes) {
    t3 = getSelectedPlatformsFromEntityTypes(selectedEntityTypes);
    $[3] = selectedEntityTypes;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const selectedPlatforms = t3;
  const resolvedInputPlaceholder = enableSkills && skillsInputPlaceholder != null && hasComposerSkills(skillsCatalog) ? skillsInputPlaceholder : inputPlaceholder;
  let t4;
  if ($[5] !== composerRef) {
    t4 = skill => {
      composerRef?.current?.insertSlashCommand({
        id: skill.id,
        name: skill.name,
        chipColor: getSkillChipColor(skill.scope)
      });
      composerRef?.current?.focus();
    };
    $[5] = composerRef;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleSelectSkill = t4;
  let t5;
  if ($[7] !== enableSkills || $[8] !== selectedPlatforms || $[9] !== skillsCatalog || $[10] !== t) {
    t5 = (_message, meta) => {
      if (!enableSkills) {
        return true;
      }
      const unavailableSkills = getUnavailableChipSkills(meta.slashCommandIds, skillsCatalog, selectedPlatforms);
      if (unavailableSkills.length === 0) {
        return true;
      }
      makeErrorToast(unavailableSkills.map(skill_0 => {
        const missingPlatforms = getMissingPlatformsForSkill(skill_0, selectedPlatforms);
        return missingPlatforms.length > 0 ? t("composer.skillNeedsDataSourceOnSubmit", {
          skill: skill_0.name,
          platform: missingPlatforms.map(getPlatformDisplayName).join(", ")
        }) : t("composer.skillUnavailableOnSubmit", {
          skill: skill_0.name
        });
      }).join(" "));
      return false;
    };
    $[7] = enableSkills;
    $[8] = selectedPlatforms;
    $[9] = skillsCatalog;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleBeforeSubmit = t5;
  let t6;
  if ($[12] !== enableSkills || $[13] !== onSubmit || $[14] !== selectedPlatforms || $[15] !== skillsCatalog) {
    t6 = (message, meta_0) => {
      const {
        outboundMessage,
        skillIds
      } = resolveComposerSkillOutbound(message, meta_0?.slashCommandIds ?? [], skillsCatalog, {
        skillsEnabled: enableSkills,
        selectedPlatforms
      });
      onSubmit(outboundMessage, {
        skillIds
      });
    };
    $[12] = enableSkills;
    $[13] = onSubmit;
    $[14] = selectedPlatforms;
    $[15] = skillsCatalog;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleSubmit = t6;
  let t7;
  bb0: {
    if (!enableSkills) {
      let t8;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    let t9;
    if ($[18] !== t) {
      t8 = knowledge => t("composer.skillsAddsKnowledge", {
        knowledge
      });
      t9 = platform => t("composer.skillsNeedsDataSource", {
        platform
      });
      $[18] = t;
      $[19] = t8;
      $[20] = t9;
    } else {
      t8 = $[19];
      t9 = $[20];
    }
    let t10;
    if ($[21] !== selectedPlatforms || $[22] !== skillsCatalog || $[23] !== t8 || $[24] !== t9) {
      t10 = buildCopilotSkillSlashCommands(skillsCatalog, selectedPlatforms, {
        addsKnowledge: t8,
        needsDataSource: t9
      });
      $[21] = selectedPlatforms;
      $[22] = skillsCatalog;
      $[23] = t8;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    t7 = t10;
  }
  const slashCommands = t7;
  let slashCommandsEmptyMessage;
  if (enableSkills) {
    if (showSkillsLoading) {
      let t8;
      if ($[26] !== t) {
        t8 = t("composer.skillsLoading");
        $[26] = t;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      slashCommandsEmptyMessage = t8;
    } else {
      if (isSkillsCatalogUnavailable) {
        let t8;
        if ($[28] !== t) {
          t8 = t("composer.skillsLoadError");
          $[28] = t;
          $[29] = t8;
        } else {
          t8 = $[29];
        }
        slashCommandsEmptyMessage = t8;
      } else {
        if (!hasComposerSkills(skillsCatalog)) {
          let t8;
          if ($[30] !== t) {
            t8 = t("composer.skillsCatalogEmpty");
            $[30] = t;
            $[31] = t8;
          } else {
            t8 = $[31];
          }
          slashCommandsEmptyMessage = t8;
        }
      }
    }
  }
  let t8;
  if ($[32] !== enableSkills || $[33] !== handleSelectSkill || $[34] !== isSkillsCatalogUnavailable || $[35] !== refetchSkills || $[36] !== selectedEntityTypes || $[37] !== showSkillsLoading || $[38] !== skillsCatalog) {
    t8 = enableSkills ? <CopilotComposerSkillsAction key="copilot-composer-skills" onSelectSkill={handleSelectSkill} selectedEntityTypes={selectedEntityTypes} skills={skillsCatalog} isLoading={showSkillsLoading} isError={isSkillsCatalogUnavailable} onRetry={() => {
      refetchSkills();
    }} /> : null;
    $[32] = enableSkills;
    $[33] = handleSelectSkill;
    $[34] = isSkillsCatalogUnavailable;
    $[35] = refetchSkills;
    $[36] = selectedEntityTypes;
    $[37] = showSkillsLoading;
    $[38] = skillsCatalog;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  const skillsAction = t8;
  let t9;
  if ($[40] !== handleBeforeSubmit || $[41] !== handleSubmit || $[42] !== resolvedInputPlaceholder || $[43] !== skillsAction || $[44] !== slashCommands || $[45] !== slashCommandsEmptyMessage) {
    t9 = {
      inputPlaceholder: resolvedInputPlaceholder,
      slashCommands,
      slashCommandsEmptyMessage,
      skillsAction,
      handleBeforeSubmit,
      handleSubmit
    };
    $[40] = handleBeforeSubmit;
    $[41] = handleSubmit;
    $[42] = resolvedInputPlaceholder;
    $[43] = skillsAction;
    $[44] = slashCommands;
    $[45] = slashCommandsEmptyMessage;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  return t9;
};